import React, { ReactNode, useCallback, useContext, useState } from 'react';
import { EnterpriseDataRoom } from './data';
import { getUserFullName } from 'utils';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import usePastTime from 'hooks/usePastTime';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import LockclosedIcon from 'icons/Lockclosed.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import useCanAccessDataRoom from 'hooks/useCanAccessDataRoom';

type CellProps = {
  dataRoom: DataRoom;
  onGrantAccess?: (id: string) => void;
};

const DealRoomCell: React.FC<CellProps> = ({ dataRoom }) => {
  const { hasDataRoomAccess, dealRoom } = dataRoom;

  const { name } = dealRoom || {};

  const canAccessDataRoom = useCanAccessDataRoom()(dealRoom, hasDataRoomAccess);

  if (!name) return null;

  return (
    <div className="dealroom-cell">
      {name} {!canAccessDataRoom && <LockclosedIcon />}
    </div>
  );
};

const OwnerCell: React.FC<CellProps> = ({ dataRoom }) => {
  const { user } = dataRoom;

  const { firstName, lastName } = user || {};

  const { t } = useTranslation();

  if (!firstName && !lastName)
    return <p className="owner-cell">{t('invited')}</p>;

  return <p className="owner-cell">{getUserFullName(firstName, lastName)}</p>;
};

const HasAccessCell: React.FC<CellProps> = ({ dataRoom, onGrantAccess }) => {
  const { _id, hasDataRoomAccess, requestedDataRoomAccess, dealRoom } =
    dataRoom;

  const [isLoading, setIsLoading] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const askForAccess = useCallback(async () => {
    try {
      setIsLoading(true);
      await api.dataRoom.access.requestAccess(_id);
      onGrantAccess(_id);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [_id, onGrantAccess, popServerError]);

  const canAccessDataRoom = useCanAccessDataRoom()(dealRoom, hasDataRoomAccess);

  if (canAccessDataRoom)
    return <p className="access-cell granted">{t('granted')}</p>;

  if (requestedDataRoomAccess)
    return <p className="access-cell requested">{t('requested')}</p>;

  return (
    <Button
      className="access-cell"
      disabled={isLoading}
      onClick={(e) => {
        e.stopPropagation();
        askForAccess();
      }}
    >
      {t('askForAccess')}
    </Button>
  );
};

const CreatedAtCell: React.FC<CellProps> = ({ dataRoom }) => {
  const { createdAt } = dataRoom;

  const pastTime = usePastTime();

  if (!createdAt) return null;

  return <p className="created-at-cell">{pastTime(createdAt)}</p>;
};

export const BODY_CONTENT_GENERATE: Record<
  keyof EnterpriseDataRoom,
  (cellProps: CellProps) => ReactNode
> = {
  dealRoom: (cellProps: CellProps) => <DealRoomCell {...cellProps} />,
  user: (cellProps: CellProps) => <OwnerCell {...cellProps} />,
  hasDataRoomAccess: (cellProps: CellProps) => <HasAccessCell {...cellProps} />,
  createdAt: (cellProps: CellProps) => <CreatedAtCell {...cellProps} />,
};
